import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { flowMachine, flowReducer } from './flowMachine'
import {
  VerificationFlowContextType,
  VerificationFlowOptions,
  VerificationFlowState,
  PartnerType,
  FlowType
} from './types'
import { TopicKey } from '../../../constants/customerMessages'
import { selectCustomerIsLoanOfficer } from '../../../store/selectors/customerProfile'

// Default options
const defaultOptions: VerificationFlowOptions = {
  enableVerificationFlow: false,
  initialPartnerType: 'RealEstateAgent',
  realEstateAgentMembership: null,
  loanOfficerMembership: null,
  initialVerificationState: false,
  triggerOnMessageSent: true,
  verificationTriggerTopics: [],
  includePrequal: false,
  prequalOnly: false
}

// Create context
const VerificationFlowContext = createContext<VerificationFlowContextType | undefined>(undefined)

interface VerificationFlowProviderProps {
  children: React.ReactNode
  options?: Partial<VerificationFlowOptions>
}

export const VerificationFlowProvider = ({ children, options: userOptions = {} }: VerificationFlowProviderProps) => {
  const options = { ...defaultOptions, ...userOptions }

  // Determine customer profile data internally using Redux selectors
  const customerIsLoanOfficer = useSelector(selectCustomerIsLoanOfficer)

  // Initialize state
  const initialState: VerificationFlowState = {
    currentStep: flowMachine.initial,
    partnerType: options.initialPartnerType,
    currentPartnerType: options.initialPartnerType,
    flowType: options.flowType,
    realEstateAgentMembership: options.realEstateAgentMembership,
    loanOfficerMembership: options.loanOfficerMembership,
    isVerified: options.realEstateAgentMembership?.isVerified || false,
    includePrequal: options.includePrequal || false,
    prequalOnly: options.prequalOnly || false,
    customerIsLoanOfficer: customerIsLoanOfficer,
    onComplete: options.onComplete || (() => {}),
    onSkip: options.onSkip || (() => {})
  }

  const [state, dispatch] = useReducer(flowReducer, initialState)

  // Update customer profile data when it changes
  useEffect(() => {
    if (customerIsLoanOfficer !== state.customerIsLoanOfficer) {
      dispatch({
        type: 'UPDATE_CUSTOMER_PROFILE',
        payload: {
          customerIsLoanOfficer
        }
      })
    }
  }, [customerIsLoanOfficer, state.customerIsLoanOfficer])

  // Update memberships when they change after initialization
  useEffect(() => {
    if (options.realEstateAgentMembership !== state.realEstateAgentMembership) {
      dispatch({
        type: 'UPDATE_MEMBERSHIP',
        payload: {
          membership: options.realEstateAgentMembership,
          partnerType: 'RealEstateAgent'
        }
      })
    }
  }, [options.realEstateAgentMembership, state.realEstateAgentMembership])

  useEffect(() => {
    if (options.loanOfficerMembership !== state.loanOfficerMembership) {
      dispatch({
        type: 'UPDATE_MEMBERSHIP',
        payload: {
          membership: options.loanOfficerMembership,
          partnerType: 'LoanOfficer'
        }
      })
    }
  }, [options.loanOfficerMembership, state.loanOfficerMembership])

  // Helper function to start verification with a specific partner type
  const startVerification = (
    message: string,
    verifiedTopicKey: TopicKey,
    defaultTopicKey: TopicKey,
    partnerType?: PartnerType,
    flowType?: FlowType
  ) => {
    const reaMembership = options.realEstateAgentMembership
    const loMembership = options.loanOfficerMembership
    const targetPartnerType = partnerType || options.initialPartnerType
    const targetFlowType = flowType || options.flowType

    // If verification flow is disabled or there's no membership to verify,
    // skip verification and send the message directly with the default topic key.
    // The default topic key is what mikasa uses to control which customer
    // is should receive the message.
    if (
      !options.enableVerificationFlow ||
      (targetPartnerType === 'RealEstateAgent' && !reaMembership) ||
      (targetPartnerType === 'LoanOfficer' && !loMembership && !options.prequalOnly) ||
      (!reaMembership && !loMembership)
    ) {
      dispatch({
        type: 'SEND_MESSAGE',
        payload: {
          message,
          topicKey: defaultTopicKey,
          partnerType: targetPartnerType,
          flowType: targetFlowType
        }
      })
      return
    }

    // If all else fails and verification is needed, start the verification flow
    dispatch({
      type: 'START_VERIFICATION',
      payload: {
        message,
        topicKey: verifiedTopicKey,
        partnerType: targetPartnerType,
        flowType: targetFlowType
      }
    })
  }

  return (
    <VerificationFlowContext.Provider
      value={{
        state,
        dispatch,
        startVerification
      }}
    >
      {children}
    </VerificationFlowContext.Provider>
  )
}

// Custom hook to use the verification flow context
export const useVerificationFlow = (): VerificationFlowContextType => {
  const context = useContext(VerificationFlowContext)

  if (context === undefined) {
    throw new Error('useVerificationFlow must be used within a VerificationFlowProvider')
  }

  return context
}

// Helper function to check if a topic should trigger verification
export const shouldTriggerVerification = (topicKey: string, options: VerificationFlowOptions): boolean => {
  if (!options.enableVerificationFlow || !options.triggerOnMessageSent) {
    return false
  }

  if (!options.verificationTriggerTopics || options.verificationTriggerTopics.length === 0) {
    return true // If no specific topics are defined, trigger for all
  }

  return options.verificationTriggerTopics.includes(topicKey as any)
}
